import React, { useEffect, useState } from "react";
import { Col, Row, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useTimer } from "react-timer-hook";
import { useFormik } from "formik";
import { signinRoute } from "../../../routes/routesPath";
import { getOTPExpiryTime } from "../../../utils/helpers";
import { successMsg } from "../../../utils/toastMessage";
import { emailSchema, resetPassowrdSchema } from "../../../utils/validationSchemas";
import { handleApiRequest } from "../../../services/handleApiRequest";
import { resetPassword, sendOtp } from "../../../redux/auth/thunk";
import SideSection from "../components/sideSection";
import MyForm from "../../../Component/formComponent";
import AppLogo from "../../../Component/appLogo";
import Asterik from "../../../Component/common/asterik";
import { TextInput } from "../../../Component/formComponent/components/inputFields";
import { ErrorField } from "../../../Component/common/errorField";

const resetPasswordFields = [
  {
    value: "otp",
    label: "OTP",
    placeholder: "Enter OTP",
    type: "text",
    isRequired: true,
    wrapperClassName: "my-3",
  },
  {
    value: "password",
    label: "New Password",
    placeholder: "Enter New Password",
    type: "password",
    isRequired: true,
    wrapperClassName: "my-3",
  },
  {
    value: "confirmPassword",
    label: "Confirm New Password",
    placeholder: "Confirm New Password",
    type: "password",
    isRequired: true,
    wrapperClassName: "my-3",
  },
];

const defaultValues = {
  otp: "",
  password: "",
  confirmPassword: "",
  email: "",
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const { totalSeconds, start, restart } = useTimer({
    autoStart: false,
    expiryTimestamp: getOTPExpiryTime(),
  });

  const [userDetails, setUserDetails] = useState(defaultValues);
  const [resetStep, setResetStep] = useState({ step: 1 });

  const handleSendOtp = async (type) => {
    const { values } = emailForm;
    setResetStep((prev) => ({
      ...prev,
      action: type === "resendOtp" ? "resendingOtp" : "sendingOtp",
    }));
    setUserDetails((prev) => ({ ...prev, email: values.email || prev.email }));

    const response = await handleApiRequest(sendOtp, { email: values.email || userDetails.email });
    if (response.status) {
      successMsg("OTP Sent!!");
      setResetStep((prev) => ({ ...prev, step: 2, action: "" }));
      restart(getOTPExpiryTime());
    } else {
      setResetStep((prev) => ({ ...prev, action: "" }));
    }
  };

  const handleResetPassword = async () => {
    setResetStep((prev) => ({ ...prev, action: "updatingPassword" }));

    const response = await handleApiRequest(resetPassword, values);
    setResetStep((prev) => ({ ...prev, action: "" }));

    if (response.status) {
      successMsg("Passowrd Updated!!");
      navigate(signinRoute);
    }
  };

  const emailForm = useFormik({
    initialValues: defaultValues,
    validationSchema: emailSchema,
    onSubmit: () => {
      if (resetStep?.action !== "sendingOtp") {
        handleSendOtp();
      }
    },
  });

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: defaultValues,
    validationSchema: resetPassowrdSchema,
    onSubmit: () => {
      if (resetStep?.action !== "updatingPassword") {
        handleResetPassword();
      }
    },
  });

  useEffect(() => {
    if (userDetails.email) {
      setFieldValue("email", userDetails.email);
    }
  }, [userDetails]);

  return (
    <section className="position-relative">
      <div className="">
        <Row className="authContainer m-0">
          <SideSection />
          <Col lg={5} className="authFormContainer px-3">
            <div className="w-100">
              <div className="pointer text-center mb-5" onClick={() => navigate("/")}>
                <AppLogo />
              </div>
              <div className="position-relative">
                <h4>Reset Password</h4>

                {resetStep.step === 1 ? (
                  <>
                    <p className="mb-4">
                      Enter your email that is registered with us. We will send an OTP to verify
                      your email and reset your password.
                    </p>
                    <Form className="" onSubmit={emailForm.handleSubmit}>
                      <div className="position-relative my-3">
                        <label htmlFor="email" className="text-color mb-1">
                          Email
                          <Asterik />
                        </label>

                        <TextInput
                          type="email"
                          placeholder="Enter Email Address"
                          name="email"
                          value={emailForm.values.email}
                          onChange={emailForm.handleChange}
                        />
                        <ErrorField error={emailForm.touched.email ? emailForm.errors.email : ""} />
                      </div>

                      {resetStep?.action === "sendingOtp" ? (
                        <button className="primaryBtn muted mb-3 rounded w-100 d-flex align-items-center justify-content-center py-1">
                          Sending OTP
                          <i className="growLoader spinner-grow ms-2" role="status"></i>
                        </button>
                      ) : (
                        <button type="submit" className="primaryBtn mb-3 py-1 rounded w-100">
                          Send OTP
                        </button>
                      )}
                    </Form>
                  </>
                ) : (
                  <>
                    <p>
                      We have sent a 6 digits OTP on your email. Use that to verify your email and
                      reset your password.
                    </p>

                    <Form className="" onSubmit={handleSubmit}>
                      <MyForm
                        values={values}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        handleChange={handleChange}
                        formFields={resetPasswordFields}
                        touched={touched}
                      />

                      {resetStep?.action === "updatingPassword" ? (
                        <button className="primaryBtn muted rounded w-100 d-flex align-items-center justify-content-center py-1">
                          Updating Password
                          <i className="growLoader spinner-grow ms-2" role="status"></i>
                        </button>
                      ) : (
                        <button type="submit" className="primaryBtn rounded w-100 py-1">
                          Reset Password
                        </button>
                      )}
                    </Form>

                    <div className="text-medium mt-3">
                      <p className="m-0">
                        Didn't receive OTP?
                        {totalSeconds > 0 ? (
                          <span className="text-primary"> {totalSeconds} sec</span>
                        ) : resetStep.action === "resendingOtp" ? (
                          <button className="textBtn text-primary">Sending OTP...</button>
                        ) : (
                          <button
                            className="textBtn text-primary fw-bold"
                            onClick={() => {
                              handleSendOtp("resendOtp");
                              start();
                            }}>
                            Resend
                          </button>
                        )}
                      </p>
                    </div>
                  </>
                )}

                <div className="">
                  <p className="mb-0">
                    Remember Password?
                    <Link to={signinRoute} className="text-decoration-none">
                      {" "}
                      Login
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
}
